// Renderer choices match the shared Go vocabulary. GraphQL accepts historical strings.
export const DiagramNodeEmphasis = {
  Low: 'LOW',
  Medium: 'MEDIUM',
  High: 'HIGH',
} as const;
export const DiagramNodeDisplay = {
  Tile: 'TILE',
  TileIcon: 'TILE_ICON',
  Shape: 'SHAPE',
} as const;
export const DiagramNodeIcon = {
  Person: 'person',
  House: 'house',
  Heart: 'heart',
  Currency: 'currency',
  Document: 'document',
  // The People icon.
  Family: 'family',
  Charity: 'charity',
  Entity: 'entity',
  Subtrust: 'subtrust',
  Account: 'account',
  Organization: 'organization',
  Hidden: 'hidden',
} as const;
export const DiagramNodeColor = {
  Primary: 'primary',
  Secondary: 'secondary',
  Tertiary: 'tertiary',
  Destructive: 'destructive',
  External: 'external',
  Default: 'default',
} as const;
export const DiagramNodeFontSize = {
  Sm: 'SM',
  Md: 'MD',
  Lg: 'LG',
  Xl: 'XL',
  Xxl: 'XXL',
} as const;
export const DiagramNodeIconShape = {
  Circle: 'CIRCLE',
  Square: 'SQUARE',
  Triangle: 'TRIANGLE',
  Diamond: 'DIAMOND',
} as const;
export const DiagramEdgeColor = {
  Primary: 'primary',
  Secondary: 'secondary',
  Destructive: 'destructive',
  Accent: 'accent',
} as const;
export const DiagramEdgeLineStyle = {
  Solid: 'solid',
  Dashed: 'dashed',
  Dotted: 'dotted',
} as const;
export const DiagramEdgeRoute = {
  Elbow: 'elbow',
  Straight: 'straight',
  Curved: 'curved',
} as const;
export const DiagramEdgeArrowheads = {
  None: 'none',
  End: 'end',
  Start: 'start',
  Both: 'both',
} as const;
/** The `nodeStyle.shape` of a node drawn as a shape. Rounded is a rectangle. */
export const DiagramNodeShape = {
  Circle: 'circle',
  Triangle: 'triangle',
  Diamond: 'diamond',
  Square: 'square',
  Rounded: 'rounded',
} as const;
/** The body of a node drawn as a shape. Null means filled. */
export const DiagramNodeFill = {
  Filled: 'filled',
  Outlined: 'outlined',
} as const;

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Returns true for an opaque `#RGB` or `#RRGGBB` color. A node or edge
 * `color` is a palette token or one of these colors.
 */
export function isDiagramHexColor(
  color: string | null | undefined
): color is string {
  return !!color && HEX_COLOR.test(color);
}

export type DiagramNodeEmphasis =
  (typeof DiagramNodeEmphasis)[keyof typeof DiagramNodeEmphasis];
export type DiagramNodeIcon =
  (typeof DiagramNodeIcon)[keyof typeof DiagramNodeIcon];
export type DiagramNodeDisplay =
  (typeof DiagramNodeDisplay)[keyof typeof DiagramNodeDisplay];
export type DiagramEdgeArrowheads =
  (typeof DiagramEdgeArrowheads)[keyof typeof DiagramEdgeArrowheads];
export type DiagramNodeShape =
  (typeof DiagramNodeShape)[keyof typeof DiagramNodeShape];
export type DiagramNodeFill =
  (typeof DiagramNodeFill)[keyof typeof DiagramNodeFill];
export type DiagramNodeIconShape =
  (typeof DiagramNodeIconShape)[keyof typeof DiagramNodeIconShape];

// Pixel tiles and text notes are renderer-only extensions.
export const TileVariant = { ...DiagramNodeColor, Pixel: 'pixel' } as const;
export type TileVariant = (typeof TileVariant)[keyof typeof TileVariant];
export const GraphVizNodeComponentType = {
  ...DiagramNodeDisplay,
  TextNote: 'TEXT_NOTE',
} as const;
export type GraphVizNodeComponentType =
  (typeof GraphVizNodeComponentType)[keyof typeof GraphVizNodeComponentType];

// A rounded square draws the builder's `rounded` node shape. It is not an
// `iconShape` value of the shared vocabulary.
export const GraphVizIconShape = {
  ...DiagramNodeIconShape,
  Rounded: 'ROUNDED',
} as const;
export type GraphVizIconShape =
  (typeof GraphVizIconShape)[keyof typeof GraphVizIconShape];

/**
 * The outline that draws each `nodeStyle.shape`. The contract also accepts
 * some older shape names; callers draw these as a rectangle.
 */
export const ICON_SHAPE_BY_NODE_SHAPE: Record<
  DiagramNodeShape,
  GraphVizIconShape
> = {
  [DiagramNodeShape.Circle]: GraphVizIconShape.Circle,
  [DiagramNodeShape.Triangle]: GraphVizIconShape.Triangle,
  [DiagramNodeShape.Diamond]: GraphVizIconShape.Diamond,
  [DiagramNodeShape.Square]: GraphVizIconShape.Square,
  [DiagramNodeShape.Rounded]: GraphVizIconShape.Rounded,
};

export {
  DiagramNodeEmphasis as GraphVizTileEmphasis,
  DiagramNodeIcon as SemanticTileKind,
};

// String unions also accept existing renderer callers and stored string values.
export type TileFontSizeVariant =
  (typeof DiagramNodeFontSize)[keyof typeof DiagramNodeFontSize];
export type ArrowEdgeVariant =
  (typeof DiagramEdgeColor)[keyof typeof DiagramEdgeColor];
export type ArrowEdgeStyleVariant =
  (typeof DiagramEdgeLineStyle)[keyof typeof DiagramEdgeLineStyle];
export type ArrowEdgeRoute =
  (typeof DiagramEdgeRoute)[keyof typeof DiagramEdgeRoute];
